import { normalizeFormDateStringToIso } from '~/utils/dateFormatter';

import { normalizePayloadText } from '../htmlEntities';
import { parseNonEmptyStrings } from '../parseHelpers';

import { createDefaultCard } from './defaults';
import type { WhatsNewCarouselCard, WhatsNewCarouselPayloadBuilderState } from './types';

const PREVIEW_REQUIRED = '[required]';

function payloadText(value: string): string {
  return normalizePayloadText(value);
}

function previewRequiredValue(value: string): string {
  const normalizedValue = payloadText(value);
  return normalizedValue ? normalizedValue : PREVIEW_REQUIRED;
}

function previewDateTime(value: string): string {
  return value.trim() ? normalizeFormDateStringToIso(value) : PREVIEW_REQUIRED;
}

function buildCard(card: WhatsNewCarouselCard): Record<string, unknown> {
  const imageFit = card.imageOptionsFit.trim();
  const optimizelyFeatureFlag = payloadText(card.optimizelyFeatureFlag);

  return {
    endAt: normalizeFormDateStringToIso(card.endAt),
    ctaUrl: payloadText(card.ctaUrl),
    imageId: payloadText(card.imageId),
    startAt: normalizeFormDateStringToIso(card.startAt),
    isActive: card.isActive,
    'title-i18n': payloadText(card.title),
    openInNewTab: card.openInNewTab,
    ...(imageFit ? { imageOptions: { fit: imageFit } } : {}),
    'ctaLabel-i18n': payloadText(card.ctaLabel),
    'overline-i18n': payloadText(card.overline),
    enabledRegions: parseNonEmptyStrings(card.enabledRegions),
    'description-i18n': payloadText(card.description),
    ...(optimizelyFeatureFlag ? { optimizelyFeatureFlag } : {}),
  };
}

function buildPreviewCard(card: WhatsNewCarouselCard): Record<string, unknown> {
  const imageFit = card.imageOptionsFit.trim();
  const optimizelyFeatureFlag = payloadText(card.optimizelyFeatureFlag);

  return {
    endAt: previewDateTime(card.endAt),
    ctaUrl: previewRequiredValue(card.ctaUrl),
    imageId: previewRequiredValue(card.imageId),
    startAt: previewDateTime(card.startAt),
    isActive: card.isActive,
    'title-i18n': previewRequiredValue(card.title),
    openInNewTab: card.openInNewTab,
    ...(imageFit ? { imageOptions: { fit: imageFit } } : {}),
    'ctaLabel-i18n': previewRequiredValue(card.ctaLabel),
    'overline-i18n': previewRequiredValue(card.overline),
    enabledRegions: parseNonEmptyStrings(card.enabledRegions),
    'description-i18n': previewRequiredValue(card.description),
    ...(optimizelyFeatureFlag ? { optimizelyFeatureFlag } : {}),
  };
}

export function buildWhatsNewCarouselPayload(state: WhatsNewCarouselPayloadBuilderState): Record<string, unknown> {
  return {
    cards: state.cards.map(buildCard),
  };
}

export function buildWhatsNewCarouselPreviewPayload(
  state: WhatsNewCarouselPayloadBuilderState,
): Record<string, unknown> {
  const cards = state.cards.length > 0 ? state.cards : [createDefaultCard()];

  return {
    cards: cards.map(buildPreviewCard),
  };
}

export function serializeWhatsNewCarouselPayload(state: WhatsNewCarouselPayloadBuilderState): string {
  return JSON.stringify(buildWhatsNewCarouselPayload(state), null, 2);
}

export function serializeWhatsNewCarouselPreviewPayload(state: WhatsNewCarouselPayloadBuilderState): string {
  return JSON.stringify(buildWhatsNewCarouselPreviewPayload(state), null, 2);
}

export function whatsNewCarouselPayloadDownloadFilename(): string {
  return 'whats-new-carousel-payload.txt';
}
